/**
 * Ontology Chat Component (v1.0)
 *
 * Natural language interface for querying FAIR knowledge graph.
 * Provides chat interface with provenance display.
 *
 * Design: Documentation/Development/EPIC-FAIR-GraphRAG-Chat.md
 *
 * @module client/components/OntologyChat
 * @version 1.0
 * @date 2025-10-11
 */

import React, { useState, useRef, useEffect } from 'react';
import './OntologyChat.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  provenance?: {
    query: string;
    explanation: string;
    confidence: 'high' | 'medium' | 'low';
    executionTimeMs: number;
    resultCount: number;
  };
  results?: Record<string, unknown>[];
}

interface OntologyChatProps {
  jobId: string;
  onClose?: () => void;
}

export function OntologyChat({ jobId, onClose }: OntologyChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedProvenance, setSelectedProvenance] = useState<Message['provenance'] | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`/api/ontology/${jobId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question: input,
          maxResults: 100
        })
      });

      if (!response.ok) {
        throw new Error(`Chat API error: ${response.status}`);
      }

      const data = await response.json();

      const assistantMessage: Message = {
        role: 'assistant',
        content: data.answer,
        timestamp: new Date().toISOString(),
        provenance: data.provenance,
        results: data.results
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);

      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your question. Please try again.',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (question: string) => {
    setInput(question);
  };

  const suggestions = [
    'Show me all failed FAIR rules',
    'What rules are in the Findable pillar?',
    'Which datasets have persistent identifiers?',
    'Show me license information'
  ];

  return (
    <div className="ontology-chat">
      {/* Header */}
      <div className="ontology-chat__header">
        <h2 className="ontology-chat__title">Ask FAIR AI</h2>
        <p className="ontology-chat__subtitle">Query your FAIR knowledge graph using natural language</p>
        {onClose && (
          <button
            className="ontology-chat__close"
            onClick={onClose}
            data-action="close-chat"
          >
            &times;
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="ontology-chat__messages">
        {messages.length === 0 && (
          <div className="ontology-chat__welcome">
            <h3>Welcome to FAIR GraphRAG Chat</h3>
            <p>Ask questions about your FAIR analysis results. I'll query the knowledge graph and provide answers with full provenance.</p>
            <div className="ontology-chat__suggestions">
              <p><strong>Try asking:</strong></p>
              {suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  className="ontology-chat__suggestion"
                  onClick={() => handleSuggestionClick(suggestion)}
                  data-action="use-suggestion"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message, i) => (
          <div key={i} className={`ontology-chat__message ontology-chat__message--${message.role}`}>
            <div className="ontology-chat__message-header">
              <span className="ontology-chat__message-role">
                {message.role === 'user' ? 'You' : 'FAIR AI'}
              </span>
              <span className="ontology-chat__message-time">
                {new Date(message.timestamp).toLocaleTimeString()}
              </span>
            </div>
            <div className="ontology-chat__message-content">
              {message.content}
            </div>

            {message.provenance && (
              <div className="ontology-chat__provenance-preview">
                <button
                  className="ontology-chat__show-provenance"
                  onClick={() => setSelectedProvenance(message.provenance || null)}
                  data-action="show-provenance"
                >
                  View Query & Provenance
                </button>
                <span className="ontology-chat__confidence ontology-chat__confidence--{message.provenance.confidence}">
                  {message.provenance.confidence} confidence
                </span>
                <span className="ontology-chat__result-count">
                  {message.provenance.resultCount} results
                </span>
                <span className="ontology-chat__execution-time">
                  {message.provenance.executionTimeMs.toFixed(2)}ms
                </span>
              </div>
            )}

            {message.results && message.results.length > 0 && (
              <div className="ontology-chat__results">
                <details>
                  <summary>View Results ({message.results.length})</summary>
                  <div className="ontology-chat__results-table">
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(message.results[0]).map((key) => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {message.results.slice(0, 10).map((result, idx) => (
                          <tr key={idx}>
                            {Object.values(result).map((value: unknown, vidx) => (
                              <td key={vidx}>
                                {typeof value === 'object' && value !== null
                                  ? JSON.stringify(value, null, 2)
                                  : String(value)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {message.results.length > 10 && (
                      <p className="ontology-chat__results-more">
                        ... and {message.results.length - 10} more results
                      </p>
                    )}
                  </div>
                </details>
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="ontology-chat__message ontology-chat__message--assistant">
            <div className="ontology-chat__message-header">
              <span className="ontology-chat__message-role">FAIR AI</span>
            </div>
            <div className="ontology-chat__message-content">
              <div className="ontology-chat__loading">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="ontology-chat__input-container">
        <input
          type="text"
          className="ontology-chat__input"
          placeholder="Ask a question about your FAIR analysis..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={loading}
        />
        <button
          className="ontology-chat__send"
          onClick={handleSend}
          disabled={loading || !input.trim()}
          data-action="send-message"
        >
          Send
        </button>
      </div>

      {/* Provenance Panel */}
      {selectedProvenance && (
        <div className="ontology-chat__provenance-modal">
          <div className="ontology-chat__provenance-content">
            <div className="ontology-chat__provenance-header">
              <h3>Query Provenance</h3>
              <button
                className="ontology-chat__close"
                onClick={() => setSelectedProvenance(null)}
                data-action="close-provenance"
              >
                &times;
              </button>
            </div>

            <div className="ontology-chat__provenance-body">
              <div className="ontology-chat__provenance-section">
                <h4>Generated Cypher Query</h4>
                <pre className="ontology-chat__code">{selectedProvenance.query}</pre>
              </div>

              <div className="ontology-chat__provenance-section">
                <h4>Explanation</h4>
                <p>{selectedProvenance.explanation}</p>
              </div>

              <div className="ontology-chat__provenance-section">
                <h4>Metadata</h4>
                <dl>
                  <dt>Confidence:</dt>
                  <dd className="ontology-chat__confidence ontology-chat__confidence--{selectedProvenance.confidence}">
                    {selectedProvenance.confidence}
                  </dd>

                  <dt>Results:</dt>
                  <dd>{selectedProvenance.resultCount}</dd>

                  <dt>Execution Time:</dt>
                  <dd>{selectedProvenance.executionTimeMs.toFixed(2)}ms</dd>
                </dl>
              </div>
            </div>
          </div>
          <div
            className="ontology-chat__provenance-backdrop"
            onClick={() => setSelectedProvenance(null)}
          />
        </div>
      )}
    </div>
  );
}

export default OntologyChat;
